import React, { useEffect, useState } from "react";
import commitmentSlice from "../../../state/slices/commitmentSlice";
import validatorSlice from "../../../state/slices/validatorSlice";
import { bindActionCreators } from "redux";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import translate from "../../reusable/translate";
import Meter from "../meter/Meter";
import Tooltip from "../../materialCommitment/tooltip/Tooltip";
import { getMetersOfMeterPair } from "../../../utils/meterPairUtils";
import OperationDescription from "../../materialCommitment/operation/OperationDescription";

const Operation = ({ index, operation, availableOperations, meterRef }) => {
  const dispatch = useDispatch();
  const [theme, setTheme] = useState("");
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const [ownOperation, setOwnOperation] = useState(false);
  const [selectedOperation, setSelectedOperation] = useState({
    id: "",
    meters: []
  });
  const { language, commitment, validators } = useSelector(state => state);
  const [privateOperation, setPrivateOperation] = useState(
    operation.privateOperation || false
  );

  const {
    addMeter,
    deleteMeter,
    deleteOperation,
    changeOperationTitle,
    changeOperationPrivacy,
    changeOperationTheme
  } = bindActionCreators(commitmentSlice.actions, dispatch);

  /** Remove added meter (removes both if part of meterPair, else removes just one meter)*/
  const removeMeter = meter => {
    const metersToDelete = getMetersOfMeterPair(operation, meter);

    metersToDelete.forEach(m =>
      deleteMeter({ operationId: operation.operationId, meterId: m.meterId })
    );
  };

  /** Add new meter pair to operation (linked with meterPairId). */
  const addNewMeterPair = () => {
    const meterPairId = uuidv4();
    const blankMeter = {
      meterType_fi_FI: "",
      meterType_en_US: "",
      meterType_sv_SE: "",
      meterValueType: "",
      startingLevel: "",
      targetLevel: "",
      meterChartType: "LINE",
      isNew: true,
      meterDueDate: "",
      meterPairId
    };

    addMeter({
      meter: {
        ...blankMeter,
        meterId: uuidv4(),
        meterDueDate: "2030"
      },
      operationId: operation.operationId
    });
    addMeter({
      meter: {
        ...blankMeter,
        meterId: uuidv4(),
        meterDueDate: "2035"
      },
      operationId: operation.operationId
    });
  };

  const removeOperation = () => {
    deleteOperation(operation);
  };

  const changeAllOperationTitles = (operationId, titles) => {
    const languages = ["fi_FI", "sv_SE", "en_US"];
    languages.forEach(language => {
      changeOperationTitle({
        operationId,
        language,
        title: titles[language] || ""
      });
    });
  };
  const setOperationTheme = theme => {
    setTheme(theme);
    changeOperationTheme({
      operationId: operation.operationId,
      operationTheme: theme
    });

    //if theme was removed (theme === "") or differs from theme of selectedOperation -> reset operationTitle and selectedOperation
    const currTheme = findOperationThemeIndex(operation.operationTitle_fi_FI);
    if (!theme || theme !== currTheme) {
      setSelectedOperation({ id: "", meters: [] });
      changeAllOperationTitles(operation.operationId, {});
    }
  };
  const setOperationTitle = title => {
    let newTitle = title;
    //If own operation, make sure that title includes theme index as prefix so that the theme can be determined when editing saved commitment
    if (ownOperation) {
      const prefix = `${theme}X. `;
      newTitle = `${prefix}${title.substr(prefix.length)}`;
    }
    changeOperationTitle({
      operationId: operation.operationId,
      language: language,
      title: newTitle
    });
  };

  const setGreenDealOperation = id => {
    if (!id) {
      setSelectedOperation({
        id: "",
        meters: []
      });
      changeAllOperationTitles(operation.operationId, {});
      return;
    }
    const foundOperation = availableOperations.find(
      mappedOperation => mappedOperation.id === id
    );

    if (foundOperation) {
      setSelectedOperation(foundOperation);
    }
  };

  useEffect(() => {
    //When editing operation, find saved operation from available operations and set selectedOperation if found
    if (operation[`operationTitle_${language}`] !== "") {
      const foundOperation = availableOperations.find(
        mappedOperation =>
          mappedOperation[`operationTitle_${language}`] ===
          operation[`operationTitle_${language}`]
      );
      if (foundOperation) {
        setSelectedOperation({ ...operation, id: foundOperation.id });
        setOwnOperation(false);
      } else {
        setOwnOperation(true);
      }
    }
    setLoading(false);
  }, [availableOperations]);

  useEffect(() => {
    if (selectedOperation.id) {
      const foundOperation = availableOperations.find(
        mappedOperation => mappedOperation.id === selectedOperation.id
      );
      if (foundOperation) {
        changeAllOperationTitles(operation.operationId, {
          fi_FI: foundOperation.operationTitle_fi_FI,
          sv_SE: foundOperation.operationTitle_sv_SE,
          en_US: foundOperation.operationTitle_en_US
        });
      }
    }
  }, [selectedOperation]);

  useEffect(() => {
    changeOperationPrivacy({
      operation: operation,
      privacy: privateOperation
    });
  }, [privateOperation]);

  if (loading) return null;
  return (
    <div
      className={`${
        validators.operations
          ? "operation margin-top-bot"
          : "operation-validated margin-top-bot"
      }`}
    >
      <OperationHeaderDisplay
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        operation={operation}
        removeOperation={removeOperation}
        language={language}
      />

      {!collapsed && (
        <div>
          <div className="border-bottom">
            <OperationThemeContainer
              operation={operation}
              theme={theme}
              setTheme={setOperationTheme}
              language={language}
            />

            {ownOperation && (
              <OwnOperationContainer
                index={index}
                setOwnOperation={setOwnOperation}
                language={language}
                operation={operation}
                setOperationTitle={setOperationTitle}
                theme={theme}
                commitment={commitment}
              />
            )}

            {!ownOperation && (
              <ChooseOperationContainer
                index={index}
                operation={operation}
                availableOperations={availableOperations}
                setGreenDealOperation={setGreenDealOperation}
                setOwnOperation={setOwnOperation}
                meterRef={meterRef}
                selectedOperation={selectedOperation}
                theme={theme}
              />
            )}

            <OperationDescription
              operation={operation}
              title={"sit.commitment.operations.operationDescription.871646"}
              maxLength={3500}
              language={language}
              rows={5}
              tooltipTextKey={"tooltip.operationDescription.871646"}
              useLanguageOverride
            />

            {((selectedOperation && selectedOperation.id) || ownOperation) &&
              (selectedOperation.allowToSelectPublicity ||
                commitment.allowToSelectPublicity) && (
                <PublicitySelectionContainer
                  operation={operation}
                  privateOperation={privateOperation}
                  setPrivateOperation={setPrivateOperation}
                  language={language}
                />
              )}
          </div>

          {operation.meters.map((meter, meterIndex) => (
            <Meter
              operationMeterId={`operation-${index}_meter-${meterIndex}`}
              operation={operation}
              meter={meter}
              key={meter.meterId}
              meterIndex={meterIndex}
              removeMeter={removeMeter}
              disabled={operation.published && !meter.isNew}
            />
          ))}

          <div>
            <button
              className="add-meter-button items-center margin-top-bot"
              onClick={() => addNewMeterPair()}
            >
              {translate({
                textKey: "sit.commitmentCreation.addMeterPair",
                languageOverride: language
              })}{" "}
              <i className="fas fa-plus-circle" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Operation;

const OperationHeaderDisplay = ({
  collapsed,
  setCollapsed,
  operation,
  removeOperation,
  language
}) => {
  return (
    <div
      className="flex-row space-between items-center operation-header section"
      onClick={() => setCollapsed(!collapsed)}
    >
      <div key={collapsed} className="flex-row items-center w800">
        <i
          className={`fas ${collapsed ? "fa-chevron-up" : "fa-chevron-down"}`}
        />
        <h4>
          {operation["operationTitle_" + language] ||
            translate({
              textKey: "sit.commitmentCreation.operation",
              languageOverride: language
            })}
        </h4>
      </div>
      <button
        className="green-button w200"
        onClick={() => removeOperation(operation.operationId)}
        disabled={operation.published}
      >
        {translate({
          textKey: "sit.commitment.operations.removeOperation.871646",
          languageOverride: language
        })}
        <i className="fas fa-trash" />
      </button>
    </div>
  );
};

const OwnOperationContainer = ({
  index,
  operation,
  setOperationTitle,
  language,
  setOwnOperation,
  theme,
  commitment
}) => {
  return (
    <div className="flex-row items-center margin-top-bot">
      <div className="w200 flex-row">
        <p className="p">
          {translate({
            textKey: "sit.commitmentCreation.operationName.871646",
            languageOverride: language
          })}
        </p>
        <Tooltip
          textKey={"sit.commitmentCreation.operationNameInfo"}
          languageOverride={language}
        />
      </div>
      <div className="w800 space-between">
        <input
          className="input-text"
          onChange={e => {
            if (e.target.value.length >= 1) setOperationTitle(e.target.value);
          }}
          value={operation["operationTitle_" + language] || `${theme}X. `}
          type="text"
          placeholder="Syötä toimenpiteen nimi"
          disabled={operation.published}
        />
        <button
          className="green-button"
          onClick={() => setOwnOperation(false)}
          disabled={operation.published}
        >
          {translate({
            textKey: "sit.commitmentCreation.choosePredefinedOperation.871646",
            languageOverride: language
          })}
        </button>
      </div>
    </div>
  );
};

const ChooseOperationContainer = ({
  index,
  meterRef,
  setGreenDealOperation,
  availableOperations,
  setOwnOperation,
  operation,
  selectedOperation,
  theme
}) => {
  const { commitment, validators, language } = useSelector(state => state);
  const dispatch = useDispatch();
  const { changeMeterValidator } = bindActionCreators(
    validatorSlice.actions,
    dispatch
  );
  const findSelectedOperation = () => {
    const foundOperation = availableOperations.find(
      op => op.operationTitle_fi_FI === operation.operationTitle_fi_FI
    );
    return foundOperation.id || "";
  };
  return (
    <div className="flex-row items-center margin-top-bot ">
      <div className="w200">
        <div className="flex-row items-center">
          <p className="p">
            {translate({
              textKey: "sit.commitmentCreation.chooseOperation.871646",
              languageOverride: language
            })}
          </p>
          <Tooltip
            textKey={`tooltip.chooseOperation.871646`}
            secondaryTextKey={`tooltip.chooseOperation.${commitment.commitmentCategoryId}`}
            fallback={`tooltip.chooseOperation.general`}
            languageOverride={language}
          />
        </div>
      </div>
      <div className="w800 space-between" ref={meterRef}>
        <select
          id={`operationSelector-${index}`}
          className={`select ${validators.meter ? "" : "select-validated"}`}
          onChange={e => {
            setGreenDealOperation(e.target.value);
            changeMeterValidator(true);
          }}
          defaultValue={findSelectedOperation}
          value={selectedOperation.id || operation.operationId}
          disabled={operation.published || !theme}
        >
          <option value="">
            {translate({
              textKey: "sit.commitmentCreation.chooseOperation.871646",
              languageOverride: language
            })}
          </option>
          {availableOperations
            .sort((a, b) =>
              a.operationTitle_fi_FI.localeCompare(
                b.operationTitle_fi_FI,
                undefined,
                { numeric: true }
              )
            )
            .filter(mappedOperation =>
              mappedOperation.operationTitle_fi_FI.startsWith(theme)
            )
            .map(mappedOperation => {
              return (
                <option
                  key={mappedOperation.operationTitle_fi_FI}
                  value={mappedOperation.id}
                >
                  {mappedOperation["operationTitle_" + language]}
                </option>
              );
            })}
        </select>

        <button
          className="green-button"
          onClick={() => setOwnOperation(true)}
          disabled={operation.published || !theme}
        >
          {translate({
            textKey: "sit.commitmentCreation.createOwnOperation.871646",
            languageOverride: language
          })}
        </button>
      </div>
    </div>
  );
};

/**
 *  Attempts to find operation theme value based on operation title value.
 *  Returns either theme index value or "".
 */
const findOperationThemeIndex = operationTitle => {
  const match = operationTitle.match(/^(\d+\.\d+\.)/);
  return match ? match[0] : "";
};

const OperationThemeContainer = ({ operation, setTheme, theme, language }) => {
  const findOperationTheme = () => {
    if (!operation.operationTitle_fi_FI || theme) {
      return "";
    }
    const themeIndex = findOperationThemeIndex(operation.operationTitle_fi_FI);
    setTheme(themeIndex);
    return themeIndex;
  };

  return (
    <div className="flex-row items-center margin-top-bot">
      <div className="flex-row items-center w200">
        <p className="p">
          {translate({
            textKey: "sit.commitment.operations.operationArea.871646",
            languageOverride: language
          })}
        </p>
        <Tooltip
          textKey={"tooltip.operationTypeInfo.871646"}
          languageOverride={language}
        />
      </div>
      <div className="w800 space-between">
        <select
          className={"select"}
          onChange={e => setTheme(e.target.value)}
          disabled={operation.published}
          defaultValue={findOperationTheme()}
        >
          <option value="" key={"def"}>
            {translate({
              textKey: "sit.commitmentCreation.chooseActionArea",
              languageOverride: language
            })}
            ...
          </option>
          <optgroup
            label={translate({
              textKey: "sit.commitment.operations.operationAreas.871646.1",
              languageOverride: language
            })}
          >
            <option value="1.1.">
              {translate({
                textKey: "sit.commitment.operations.operationAreas.871646.1.1",
                languageOverride: language
              })}
            </option>
            <option value="1.2.">
              {translate({
                textKey: "sit.commitment.operations.operationAreas.871646.1.2",
                languageOverride: language
              })}
            </option>
            <option value="1.3.">
              {translate({
                textKey: "sit.commitment.operations.operationAreas.871646.1.3",
                languageOverride: language
              })}
            </option>
          </optgroup>
          <optgroup
            label={translate({
              textKey: "sit.commitment.operations.operationAreas.871646.2",
              languageOverride: language
            })}
          >
            <option value="2.1.">
              {translate({
                textKey: "sit.commitment.operations.operationAreas.871646.2.1",
                languageOverride: language
              })}
            </option>
            <option value="2.2.">
              {translate({
                textKey: "sit.commitment.operations.operationAreas.871646.2.2",
                languageOverride: language
              })}
            </option>
            <option value="2.3.">
              {translate({
                textKey: "sit.commitment.operations.operationAreas.871646.2.3",
                languageOverride: language
              })}
            </option>
          </optgroup>
          <optgroup
            label={translate({
              textKey: "sit.commitment.operations.operationAreas.871646.3",
              languageOverride: language
            })}
          >
            <option value="3.1.">
              {translate({
                textKey: "sit.commitment.operations.operationAreas.871646.3.1",
                languageOverride: language
              })}
            </option>
            <option value="3.2.">
              {translate({
                textKey: "sit.commitment.operations.operationAreas.871646.3.2",
                languageOverride: language
              })}
            </option>
          </optgroup>
          <optgroup
            label={translate({
              textKey: "sit.commitment.operations.operationAreas.871646.4",
              languageOverride: language
            })}
          >
            <option value="4.1.">
              {translate({
                textKey: "sit.commitment.operations.operationAreas.871646.4.1",
                languageOverride: language
              })}
            </option>
            <option value="4.2.">
              {translate({
                textKey: "sit.commitment.operations.operationAreas.871646.4.2",
                languageOverride: language
              })}
            </option>
            <option value="4.3.">
              {translate({
                textKey: "sit.commitment.operations.operationAreas.871646.4.3",
                languageOverride: language
              })}
            </option>
          </optgroup>
          <optgroup
            label={translate({
              textKey: "sit.commitment.operations.operationAreas.871646.5",
              languageOverride: language
            })}
          >
            <option value="5.1.">
              {translate({
                textKey: "sit.commitment.operations.operationAreas.871646.5.1",
                languageOverride: language
              })}
            </option>
            <option value="5.2.">
              {translate({
                textKey: "sit.commitment.operations.operationAreas.871646.5.2",
                languageOverride: language
              })}
            </option>
            <option value="5.3.">
              {translate({
                textKey: "sit.commitment.operations.operationAreas.871646.5.3",
                languageOverride: language
              })}
            </option>
            <option value="5.4.">
              {translate({
                textKey: "sit.commitment.operations.operationAreas.871646.5.4",
                languageOverride: language
              })}
            </option>
          </optgroup>
          <optgroup
            label={translate({
              textKey: "sit.commitment.operations.operationAreas.871646.X",
              languageOverride: language
            })}
          >
            <option value="X.1.">
              {translate({
                textKey: "sit.commitment.operations.operationAreas.871646.X.1",
                languageOverride: language
              })}
            </option>
          </optgroup>
        </select>
      </div>
    </div>
  );
};

const PublicitySelectionContainer = ({
  privateOperation,
  operation,
  setPrivateOperation,
  language
}) => {
  return (
    <div className="grid28">
      <div className="flex-row">
        <p className="p">
          {translate({
            textKey: "sit.commitmentCreation.operationIs",
            languageOverride: language
          })}
        </p>
        <Tooltip
          textKey={"sit.commitmentCreation.operationPublicityInfo"}
          languageOverride={language}
        />
      </div>
      <div>
        <div className="flex-row items-center">
          <input
            className="input-radio"
            type="radio"
            name={`publicity.${operation.operationId}`}
            onClick={() => setPrivateOperation(false)}
            defaultChecked={!privateOperation}
            disabled={operation.published}
          />
          <p className="p">
            {translate({
              textKey: "sit.commitmentCreation.public",
              languageOverride: language
            })}
          </p>
        </div>
        <div className="flex-row items-center">
          <input
            className="input-radio"
            type="radio"
            name={`publicity.${operation.operationId}`}
            onClick={() => setPrivateOperation(true)}
            defaultChecked={privateOperation}
            disabled={operation.published}
          />
          <p className="p">
            {translate({
              textKey: "sit.commitmentCreation.private",
              languageOverride: language
            })}
          </p>
        </div>
      </div>
    </div>
  );
};



// WEBPACK FOOTER //
// ./src/components/circularEconomyCommitment/operation/Operation.jsx